import {
  connectApple,
  connectAppleMusic,
  connectSpotify,
  handleRedirect
} from './auth';
import { get, post } from './client';
import { getConfig, setConfig } from './config';
import { emit, off, on } from './events';
import { clearStorage, getStoredToken, getStoredUser } from './storage';
import { isTokenExpired } from './token';
import type {
  ActionStatus,
  AddTracksToPlaylistParams,
  AddTracksToPlaylistResult,
  AuthResult,
  CampaignConfig,
  CaptureEmailParams,
  CaptureEmailResponse,
  ConnectAppleMusicOptions,
  ConnectAppleOptions,
  ConnectSpotifyOptions,
  CreateAppleMusicPlaylistParams,
  CreateAppleMusicPlaylistResult,
  CreateSpotifyPlaylistParams,
  CreateSpotifyPlaylistResult,
  FirebirdConfig,
  FirebirdEvent,
  SubmitLinkResponse
} from './types';
import { FirebirdError } from './types';

// Re-export types for consumers
export type {
  ActionStatus,
  AddTracksToPlaylistParams,
  AddTracksToPlaylistResult,
  AuthResult,
  CampaignConfig,
  CaptureEmailParams,
  CaptureEmailResponse,
  ConnectAppleMusicOptions,
  ConnectAppleOptions,
  ConnectSpotifyOptions,
  CreateAppleMusicPlaylistParams,
  CreateAppleMusicPlaylistResult,
  CreateSpotifyPlaylistParams,
  CreateSpotifyPlaylistResult,
  FirebirdConfig,
  FirebirdEvent,
  SubmitLinkResponse
};
export { FirebirdError };

let _campaignConfig: CampaignConfig | null = null;

/**
 * Initialize the Firebird SDK.
 * Fetches campaign config to validate the API key and cache sign-in options.
 */
async function init(config: FirebirdConfig): Promise<CampaignConfig> {
  setConfig(config);

  // Fetch campaign config to validate the key + campaign
  _campaignConfig = await get<CampaignConfig>(
    `/campaigns/${config.campaignSlug}`
  );

  emit('ready', _campaignConfig);
  return _campaignConfig;
}

/**
 * Capture a fan's email (and optional fields).
 * Creates or updates the user in Firebird.
 */
async function captureEmail(
  params: CaptureEmailParams
): Promise<CaptureEmailResponse> {
  const config = getConfig();

  try {
    const result = await post<CaptureEmailResponse>('/capture/email', {
      ...params,
      campaignSlug: config.campaignSlug
    });
    emit('capture:success', result);
    return result;
  } catch (err) {
    emit('capture:error', err);
    throw err;
  }
}

/**
 * Submit the full form with all configured fields.
 * Validates required fields against the campaign config.
 */
async function submitForm(
  params: CaptureEmailParams
): Promise<CaptureEmailResponse> {
  const config = getConfig();

  try {
    const result = await post<CaptureEmailResponse>('/capture/form', {
      ...params,
      campaignSlug: config.campaignSlug
    });
    emit('capture:success', result);
    return result;
  } catch (err) {
    emit('capture:error', err);
    throw err;
  }
}

/**
 * Get the cached campaign config, or fetch it if not loaded yet.
 */
async function getCampaign(): Promise<CampaignConfig> {
  if (_campaignConfig) {
    return _campaignConfig;
  }
  const config = getConfig();
  _campaignConfig = await get<CampaignConfig>(
    `/campaigns/${config.campaignSlug}`
  );
  return _campaignConfig;
}

/**
 * Check what actions the current user has completed on this campaign.
 * Requires a prior auth session (Spotify or Apple Music).
 */
async function getStatus(): Promise<ActionStatus> {
  const config = getConfig();
  return get<ActionStatus>(
    `/actions/status?campaignSlug=${encodeURIComponent(config.campaignSlug)}`,
    true
  );
}

/**
 * Trigger presave for the current user on this campaign's release.
 * Works for users authenticated via Spotify or Apple Music — the library
 * add happens automatically when the release goes live on the matching
 * platform.
 */
async function presave(): Promise<{ status: string; alreadyExists: boolean }> {
  const config = getConfig();
  return post('/actions/presave', { campaignSlug: config.campaignSlug }, true);
}

/**
 * Follow the campaign's configured artist. Uses the user's connected
 * provider — Spotify follows the Spotify artist; Apple Music adds the
 * artist's latest album to the user's library.
 */
async function followArtist(): Promise<{
  status: string;
  followId: string | null;
}> {
  const config = getConfig();
  return post(
    '/actions/follow-artist',
    { campaignSlug: config.campaignSlug },
    true
  );
}

/**
 * Follow the campaign's configured playlist. Uses the user's connected
 * provider (Spotify or Apple Music).
 */
async function followPlaylist(): Promise<{
  status: string;
  followId: string | null;
}> {
  const config = getConfig();
  return post(
    '/actions/follow-playlist',
    { campaignSlug: config.campaignSlug },
    true
  );
}

/**
 * Create a playlist in the fan's Spotify library. The fan must have
 * connected via `connectSpotify()` — calling this on an Apple-Music-only
 * session returns `FORBIDDEN`.
 *
 * Track IDs are bare Spotify track IDs (e.g. `3n3Ppam7vgaVa1iaRUc9Lp`),
 * no `spotify:track:` prefix needed.
 */
async function createSpotifyPlaylist(
  params: CreateSpotifyPlaylistParams
): Promise<CreateSpotifyPlaylistResult> {
  const config = getConfig();
  return post(
    '/actions/spotify/create-playlist',
    { ...params, campaignSlug: config.campaignSlug },
    true
  );
}

/**
 * Append tracks to a Spotify playlist previously created via
 * `createSpotifyPlaylist`. The fan must own the playlist.
 */
async function addTracksToSpotifyPlaylist(
  params: AddTracksToPlaylistParams
): Promise<AddTracksToPlaylistResult> {
  const config = getConfig();
  return post(
    '/actions/spotify/add-tracks-to-playlist',
    { ...params, campaignSlug: config.campaignSlug },
    true
  );
}

/**
 * Create a playlist in the fan's Apple Music library. The fan must have
 * connected via `connectAppleMusic()` — calling this on a Spotify-only
 * session returns `FORBIDDEN`.
 *
 * Track IDs are Apple Music catalog song IDs (numeric strings, e.g.
 * `1440857781`). Apple Music library playlists are always private — there
 * is no `isPublic` option.
 */
async function createAppleMusicPlaylist(
  params: CreateAppleMusicPlaylistParams
): Promise<CreateAppleMusicPlaylistResult> {
  const config = getConfig();
  return post(
    '/actions/apple-music/create-playlist',
    { ...params, campaignSlug: config.campaignSlug },
    true
  );
}

/**
 * Append tracks to an Apple Music playlist previously created via
 * `createAppleMusicPlaylist`. The fan must own the playlist.
 */
async function addTracksToAppleMusicPlaylist(
  params: AddTracksToPlaylistParams
): Promise<AddTracksToPlaylistResult> {
  const config = getConfig();
  return post(
    '/actions/apple-music/add-tracks-to-playlist',
    { ...params, campaignSlug: config.campaignSlug },
    true
  );
}

/**
 * Enter a sweepstakes event on this campaign.
 * Requires prior Spotify auth.
 */
async function enterSweepstakes(eventId: string): Promise<{
  status: string;
}> {
  const config = getConfig();
  return post(
    '/actions/sweepstakes',
    { campaignSlug: config.campaignSlug, eventId },
    true
  );
}

/**
 * Submit a link on this campaign. Requires prior auth.
 * Throws FirebirdError with code LIMIT_REACHED if the fan is at the
 * campaign's per-fan cap, or NOT_FOUND if the reward is not enabled.
 */
async function submitLink(url: string): Promise<SubmitLinkResponse> {
  const config = getConfig();
  return post(
    '/actions/submit-link',
    { campaignSlug: config.campaignSlug, url },
    true
  );
}

/**
 * Check if the user has an active session — a stored token that has not
 * expired. Host pages gate their sign-in UI on this, so an expired token
 * must report false, otherwise the fan is never offered a way back in.
 */
function isAuthenticated(): boolean {
  const token = getStoredToken();
  return token !== null && !isTokenExpired(token);
}

/**
 * Get the stored user from a previous auth session.
 */
function getUser(): { id: string; email: string; name: string } | null {
  return getStoredUser();
}

/**
 * Clear the stored session (token + user).
 */
function logout(): void {
  clearStorage();
  _campaignConfig = null;
}

// Public API
export const FirebirdSDK = {
  init,
  captureEmail,
  submitForm,
  connectSpotify: (options?: ConnectSpotifyOptions) => connectSpotify(options),
  connectApple: (options?: ConnectAppleOptions) => connectApple(options),
  connectAppleMusic: (options: ConnectAppleMusicOptions) =>
    connectAppleMusic(options),
  handleRedirect,
  getCampaign,
  getStatus,
  presave,
  followArtist,
  followPlaylist,
  createSpotifyPlaylist,
  addTracksToSpotifyPlaylist,
  createAppleMusicPlaylist,
  addTracksToAppleMusicPlaylist,
  enterSweepstakes,
  submitLink,
  isAuthenticated,
  getUser,
  logout,
  on: (event: FirebirdEvent, fn: (...args: unknown[]) => void) => on(event, fn),
  off: (event: FirebirdEvent, fn: (...args: unknown[]) => void) =>
    off(event, fn)
};

// Default export for ESM
export default FirebirdSDK;
